import styles from './PcSections.module.scss';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate, useOutletContext } from "react-router";
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import { HiChevronLeft as LeftArrowIcon, HiChevronRight as RightArrowIcon, HiPencil as PencilIcon } from 'react-icons/hi';
import Stepper from '@mui/material/Stepper';
import { Step, StepButton, StepConnector, StepIconProps, StepLabel, stepConnectorClasses } from '@mui/material';
import { styled } from '@mui/system';
import { useAppDispatch, useAppSelector } from '../../../redux/reduxHooks';
import AccountUpdate from '../accountUpdate/AccountUpdate';
import { IpRestS } from '../../../rest/ipRest-service';
import { AccessTokens, PcJourneyStatus, PcSectionSteps, ProductFetchingProgress, TaxTypes, TypeOfFinanceOption, financeTabNames, missingCostItemsInf } from '../../../interfaces/interfaces';
import { RestS } from '../../../rest/rest-service';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import Connections from '../../Connections/Connections';
import useModal from '../../../components/Hooks/useModal';
import { useSearchParams } from 'react-router-dom';
import { getLast12CYMonthsArr, getLocalCompanyId, getLocalUser } from '../../../components/functions/utilities';
import Integrations from '../../../components/common/Integrations/Integration';
import OtherSections from '../otherSections/OtherSections';
import PcAccess from '../PcAccess/PcAccess';
import ProductCosts from '../ProductCosts/ProductCosts';
import { RiStore2Line as SalesIcon, RiRefreshLine as UpdateIcon } from 'react-icons/ri'
import { GoPackage as ProductCostsIcon } from 'react-icons/go'
import { MdOutlinePayments as ExpenseIcon } from 'react-icons/md';
import moment from 'moment';
import useSocketOnPc from '../../../components/Hooks/useSocketPc';
import { HiArrowNarrowRight as RightLongArrowIcon } from 'react-icons/hi';
import { MdLockOutline as LockIcon } from 'react-icons/md'
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { FaCircleExclamation as Exclamation } from "react-icons/fa6";
import { OverlayTrigger } from 'react-bootstrap';
import { RenderSideBarTooltip } from '../../../components/common/RenderTooltip';
import { GoalsHubTooltip } from '../../ReportDetails/Components/ReportTabs/TabsContent/reportDataPiece/tooltip/Tooltip.styled';
import { fetchProductMissingCosts, getMissingCostItemsThunk, setRefreshAccountUpdate } from '../../../redux/financeStatement/financeStatement';
import CopyCosts from '../ProductCosts/CopyCosts';
import FinanceBanner from '../statement/FinanceTable/FinanceBanner';
import usePageTitle from '../../../components/Hooks/usePageTitle';
import { RiCoinsLine as CoinsIcon } from 'react-icons/ri'
import { IoMdRefresh } from "react-icons/io";


export enum PcSectionRoutes {
    editAccess = '/pnltracker/edit/access',
    updateShopify = '/pnltracker/edit/account',
    salesChannels = '/pnltracker/edit/sales-channels',
    directExpenses = '/pnltracker/edit/direct-expenses',
    otherExpenses = '/pnltracker/edit/other-expenses',
    productCosts = '/pnltracker/edit/product-costs',
    copyCosts = '/pnltracker/edit/copy-costs',
}


const initialStepperList = [
    {
        name: PcSectionSteps.editAccess,
        icon: <PencilIcon />,
        showRedDot: false,
        disabled: false,
        link: PcSectionRoutes.editAccess
    },
    {
        name: PcSectionSteps.updateShopify,
        icon: <UpdateIcon />,
        showRedDot: true,
        disabled: false,
        link: PcSectionRoutes.updateShopify,
    },
    {
        name: PcSectionSteps.addSalesChannels,
        icon: <SalesIcon />,
        showRedDot: false,
        disabled: false,
        link: PcSectionRoutes.salesChannels
    },
    {
        name: PcSectionSteps.addExpenses,
        icon: <ExpenseIcon />,
        showRedDot: false,
        disabled: false,
        link: PcSectionRoutes.directExpenses
    },
    {
        name: PcSectionSteps.otherExpenses,
        icon: <CoinsIcon />,
        showRedDot: false,
        disabled: false,
        link: PcSectionRoutes.otherExpenses
    },
    {
        name: PcSectionSteps.addProductCosts,
        icon: <ProductCostsIcon />,
        showRedDot: false,
        disabled: false,
        link: PcSectionRoutes.productCosts
    }
]

const CustomConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.active}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#100828',
            borderWidth: '2px'
        },
    },
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 14,
        left: 'calc(-50% + 16px)',
        right: 'calc(50% + 16px)',
    },
    [`&.${stepConnectorClasses.root}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#d4d4d4',
            borderWidth: '2px'
        },
    },
    [`&.${stepConnectorClasses.completed}`]: {
        '& .MuiStepConnector-line': {
            borderColor: '#100828',
            borderWidth: '2px'
        },
    }
}));

const CustomStepper = styled(Stepper)({
    // Target the alternative label style for all StepLabel components within this Stepper
    '.MuiStepLabel-labelContainer .MuiStepLabel-label': {
        marginTop: '0.5rem !important', // Remove the margin-top
    },
});

const CustomStepIcon = (props) => {
    const { active, completed, icon, disabled } = props
    return (
        <div className={`${styles.customStepIcon} ${disabled ? styles.disabled : active || completed ? styles.active : ''} `}>
            {active ?
                <div>
                    <PencilIcon />
                </div>
                : disabled ? <div>
                    <LockIcon />
                </div> :
                    <div className={`${styles.numberIcon} ${active || completed ? styles.active : ''}`}>
                        {icon}
                    </div>
            }
        </div>
    );
}

type Props = {
    isFirstTimeUser: boolean;
}


const PcSections = ({ isFirstTimeUser }: Props) => {
    // const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);

    const [activeStep, setActiveStep] = useState(0);
    const [completedSteps, setCompletedSteps] = useState({ 0: true });
    const { pathname } = useLocation();
    const [loading, setLoading] = useState(true);
    const [routeLoading, setRouteLoading] = useState(true);
    const [costMissingFilter, setCostMissingFilter] = useState(false);

    const [productFetchingLoading, setProductFetchingLoading] = useState(false);
    const pageTitle = usePageTitle('P&L Tracker | Merito');

    useEffect(() => {
        return () => {
            pageTitle('')
        }
    }, [])


    const [dfInProgress, setDfInProgress] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const { toggle: connectionToggle, visible: connectionVisible } = useModal();
    const currentUser = getLocalUser();

    const [tabsData, setTabsData] = useState({
        tabs: null,
        taxInput: null,
        financeCategories: null,
        loanData: []
    });
    const [initialLoad, setInitiaLoad] = useState(true);
    const [initialRouteCheck, setInitialRouteCheck] = useState(true);

    const [expiredAccountsCount, setExpiredAccounts] = useState({});

    const [financeStatementOptions, setFinanceStatementOptions] = useState([]);
    const [metricOptions, setMetricOptions] = useState([]);

    const [productCopyState, setProductCopyState] = useState(false);

    const applyCostsRef = useRef(null);

    const socketPc = useSocketOnPc();

    const [stepperList, setStepperList] = useState(initialStepperList);
    // const [missingProductCost, setMissingProductCost] = useState<{ productCount: number, missingCostCount: number }>({ productCount: 0, missingCostCount: 0 });
    const [showMissingCostPopup, setShowMissingCostPopup] = useState(false);
    const [showTaxesMissingPopup, setShowTaxesMissingPopup] = useState(false);
    const [skipToConfig, setSkipToConfig] = useState({ activeStep: 0, link: '', isFinish: false });
    const [isFetchingCompleted, setIsFetchingCompleted] = useState(false);

    const [productFetchingStatus, setProductFetchingStatus] = useState<{ status: ProductFetchingProgress, isProductUpdate: boolean, isJobPresent: boolean }>({
        status: null,
        isProductUpdate: false,
        isJobPresent: false,
    })

    const taxFormRef = useRef(null);
    const scrollToTaxRef = useRef(null);

    const [activeTab, setActiveTab] = useState(null);

    const dummyRef = useRef(null);
    const { setHeaderContent, resetHeaderContent } = useOutletContext<any>();
    const [amzAdsData, setAmzAdsData] = useState([]);
    const [prdCostUpdateAvailable, setPrdCostUpdateAvailable] = useState(false);
    const [missingCostsLoading, setMissingCostsLoading] = useState(false);

    useEffect(() => {
        dispatch(getMissingCostItemsThunk())
        dispatch(fetchProductMissingCosts())
    }, [])

    useEffect(() => {
        // if(showBanner) {
        setHeaderContent(<FinanceBanner showBanner={false} />)
        return resetHeaderContent;
    }, []);


    // useEffect(() => {
    //     if (isFirstTimeUser) {
    //         joinRoom();
    //     }
    // }, [])

    useEffect(() => {
        // if (update?.updateAvailable) {
        const bodyElement = document.getElementsByTagName("body")[0];
        bodyElement.style.overflowY = "hidden";
        const pageIndexLayout: any = document.getElementById("page_body");
        if (pageIndexLayout) {
            pageIndexLayout.style.overflowY = "hidden";
        }

        return () => {
            // console.log('running this')
            const bodyElement = document.getElementsByTagName("body")[0];
            const pageIndexLayout: any = document.getElementById("page_body");
            if (pageIndexLayout) {
                pageIndexLayout.style.overflowY = "scroll";
                pageIndexLayout.style.padding = "1rem 1.5rem 2rem 1.5rem";
            }
        };
        // }
    }, []);


    const clearSearchParam = () => {
        // console.log('searchParams', searchParams);
        console.log("RUNNING clear search params", searchParams)
        // const currentHash = window.location.hash;
        // Preserve the current hash

        if (searchParams.toString().length > 0) {
            searchParams.delete("customers");
            searchParams.delete("ip");
            searchParams.delete("step");
            searchParams.delete("success");
            setSearchParams(searchParams, { replace: true });

            // Check if there was a hash and it's been removed
            // if (currentHash && window.location.hash !== currentHash) {
            //     window.location.hash = currentHash; // Restore the hash manually
            // }
        }
    };



    const navigate = useNavigate();

    const handleStep = (step: number) => {
        const previousActiveStep = activeStep;
        const completedObj = handleCompletedDummySteps(step, previousActiveStep);
        // console.log('completedObj', completedObj)
        setActiveStep(step);
        setCompletedSteps(prev => ({ ...prev, ...completedObj, [step]: true }))
    };

    const stepperLocationObj = useMemo(() => {
        const locationObj = stepperList.reduce((prev, curVal, index) => {
            prev[curVal.link] = {
                ...curVal,
                stepIndex: index
            };
            return prev;
        }, {});
        return locationObj
    }, [stepperList]);



    const handleCompletedDummySteps = (currentStep: number, previousActiveStep: number) => {
        let completedObj = {};
        if (currentStep - previousActiveStep > 1) {
            // console.log('coming inside')
            const dummArr = new Array(currentStep + 1).fill("empty");
            dummArr.forEach((item, index) => {
                completedObj[index] = true;
            })
        };
        return completedObj
    }


    const handleAccessRouting = async () => {
        try {
            const { isInActive, isInProgress, isUpdateAvailable, jobStatus } = await RestS.checkJourneyRouteAccess();
            return {
                isInActive, isInProgress, isUpdateAvailable, jobStatus
            }
        } catch (error) {
            console.log('error', error);
        }


    }





    useEffect(() => {
        let activeStepIndex = stepperLocationObj[pathname.toLowerCase()];
        // console.log('activeStepIndex', activeStepIndex);
        if (loading) return;
        // if (!pathname.includes('product-costs') && productCopyState) {
        //     setProductCopyState(false);
        // }

        (async () => {
            if (isFirstTimeUser) {
                if (initialRouteCheck) {

                    const accessRouting = await handleAccessRouting()
                    // const missingCostData = await RestS.fetchProductMissingCosts()
                    // setMissingProductCost(missingCostData);
                    // setMissingCostsLoading(true);
                    // RestS.fetchProductMissingCosts().then((missingCosts) => {
                    //     setMissingProductCost(missingCosts);
                    //     setMissingCostsLoading(false);
                    // }).catch((error) => {
                    //     setMissingCostsLoading(false);
                    //     console.log('error', error)
                    // })
                    if (accessRouting?.isUpdateAvailable || accessRouting.isInActive && activeStepIndex != 0) {
                        // navigate('/profitability-calculator')
                        // console.log('accessRouting', accessRouting)
                        const allowedPaths = new Set([PcSectionRoutes.editAccess, PcSectionRoutes.updateShopify]) as Set<string>;
                        if (!allowedPaths.has(pathname.toLowerCase())) {
                            activeStepIndex = 0;
                            navigate(stepperList[0].link, { replace: true });
                        }
                    } else {
                        const accessRoutingStatus = accessRouting?.jobStatus;
                        const allowedPaths = new Set([PcSectionRoutes.editAccess, PcSectionRoutes.updateShopify, PcSectionRoutes.salesChannels, PcSectionRoutes.directExpenses]) as Set<string>;
                        if (accessRoutingStatus == ProductFetchingProgress.inProgress || accessRoutingStatus == ProductFetchingProgress.failed) {
                            if (!allowedPaths.has(pathname.toLowerCase())) {
                                activeStepIndex = 0;
                                navigate(stepperList[0].link, { replace: true });
                            }
                        }
                        if (stepperLocationObj[pathname.toLowerCase()] == undefined) {
                            if (!pathname.includes('copy-costs')) {
                                activeStepIndex = 0;
                                navigate(stepperList[0].link, { replace: true });
                            }
                        }
                    }
                }
                if (stepperLocationObj[pathname.toLowerCase()] == undefined) {
                    // console.log('coming in this block')
                    if (pathname.includes('copy-costs')) {
                        if (routeLoading) setRouteLoading(false);
                        return;
                    }
                    navigate(stepperList[0].link, { replace: true });
                    activeStepIndex = 0;
                    return;
                }
                if (Object.keys(activeStepIndex).length == 0) {
                    navigate(stepperList[0].link, { replace: true });
                    activeStepIndex = 0;
                    return;
                } else {
                    setActiveStep(activeStepIndex['stepIndex'])
                    const completedObj = handleCompletedDummySteps(activeStepIndex['stepIndex'], 0);
                    setCompletedSteps(prev => ({ ...prev, ...completedObj, [activeStepIndex['stepIndex']]: true }))
                }



            } else {

                if (activeStepIndex == undefined && !pathname?.includes('copy-costs')) {
                    navigate(PcSectionRoutes.salesChannels, { replace: true });
                    return;
                }
                if (pathname?.includes('copy-costs')) {
                    setActiveTab(PcSectionSteps.addProductCosts)
                } else {
                    setActiveTab(activeStepIndex['name'])
                }

            }
            if (routeLoading) {
                setRouteLoading(false);
            }
            setInitialRouteCheck(false);

        })();

    }, [pathname, stepperList, loading]);

    const { integrationPartners } = useAppSelector((state) => state.config);
    const { connectionStatus } = useAppSelector((state) => state.connections);
    const { refreshFinanceStatement, missingCostItems, missingProductCosts } = useAppSelector(state => state.financeStatement)

    useEffect(() => {
        if (missingProductCosts?.status == "FETCHING") {
            setMissingCostsLoading(true)
        } else {
            setMissingCostsLoading(false)
        }
    }, [missingProductCosts?.status])


    useEffect(() => {
        if (refreshFinanceStatement) {
            (async () => {
                const missingCostData = await RestS.fetchProductMissingCosts()
                // setMissingProductCost(missingCostData);
            })()
        }
    }, [refreshFinanceStatement])


    const ipConfigs = useMemo(() => {
        if (integrationPartners != undefined) {
            let configObj = {};
            const connectionStatusSet = new Set(connectionStatus);

            for (const ip of Object.values(integrationPartners)) {
                // console.log('ip', ip)
                const logo = ip['logo'];
                const ipId = ip["_id"]
                // if(connectionStatusSet.has(ip['_id'])) {
                configObj[ip['companyName']] = {
                    connectionStatus: connectionStatusSet.has(ipId) ? true : false,
                    logo
                };
                // } 
            }
            return configObj;
        } else {
            return {};
        }
    }, [integrationPartners, connectionStatus]);

    const [ipMap, setIpMap] = useState({});
    const [update, setUpdate] = useState({
        'integrationPartnerDetail': {} as any,
        'updateAvailable': false,
        'isConnected': false
    });

    const allConnectedAccounts = useAppSelector(
        (state) => state.accessTokens,
    ).accessTokens;

    const getTabsData = async () => {
        try {
            const data: any = await RestS.getFinanceTabsData();
            const tabsData = new Set<string>();

            if (data) {
                if (data?.amazonAdsData) {
                    setAmzAdsData(data?.amazonAdsData);
                }
                const groupedData = data?.tabs.reduce((prev, curVal) => {
                    if (prev[curVal.feTab] == undefined) {
                        prev[curVal.feTab] = [curVal]
                    } else {
                        prev[curVal.feTab] = [...prev[curVal.feTab], curVal]
                    }
                    tabsData.add(curVal.feTab);
                    return prev;
                }, {});


                // console.log('groupedData', groupedData);
                // console.log("tabs", Array.from(tabsData));
                setTabsData({
                    tabs: groupedData,
                    taxInput: data?.taxInput,
                    financeCategories: data?.financeCategories,
                    loanData: data?.loanData
                })
            }
            if (initialLoad) {
                const data = await RestS.getExpiredAccountsCount();
                setExpiredAccounts(data);
                setInitiaLoad(false);
            }
        } catch (error) {
            console.log('error', error);
        }
    }


    useEffect(() => {
        let tempIpMap = {};
        // setLoading(true);

        const isProductUpdateState = searchParams.get("showProductState");
        (async () => {
            try {
                await IpRestS.getAllIp().then(async (res: any) => {
                    tempIpMap = res.reduce((accumulator, currValue) => {
                        accumulator[currValue._id] = currValue;
                        return accumulator;
                    }, {})
                    setIpMap(tempIpMap);
                    let isShopifyConn = false;
                    let updateObj = { ...update };
                    const response: any = await RestS.getUserAccessTokensByUserId(
                        Number(currentUser?.companyId)
                    );
                    // console.log('accessTokens', response)
                    response?.map((acc: AccessTokens) => {
                        if (acc?.updateAvailable && tempIpMap[acc?.integrationPartner].companyName == 'Shopify') {
                            updateObj = {
                                ...updateObj,
                                'integrationPartnerDetail': tempIpMap[acc?.integrationPartner],
                                'updateAvailable': acc?.updateAvailable
                            }
                        }
                        if (tempIpMap[acc?.integrationPartner].companyName == 'Shopify') {
                            isShopifyConn = true;
                            updateObj = {
                                ...updateObj,
                                'isConnected': true,
                            }
                        }
                    });
                    // console.log('updateObj', updateObj);
                    setUpdate(prev => ({ ...prev, ...updateObj }))

                    const data: any = await RestS.getDfStatusByIp("Shopify");
                    const productFetchingStatus = await RestS.fetchProductCostUpdateStatus();
                    let isProductFetching = false;
                    if (productFetchingStatus?.isJobPresent && productFetchingStatus?.status == ProductFetchingProgress.inProgress) {
                        isProductFetching = true
                    }

                    let manipulatedStepperList = stepperList;
                    if (updateObj?.updateAvailable || data?.isInProgress) {
                        const tabsName = new Set([PcSectionSteps.editAccess, PcSectionSteps.updateShopify]);
                        manipulatedStepperList = stepperList.map((item, index) => {
                            if (tabsName.has(item.name)) {
                                return {
                                    ...item,
                                    disabled: false
                                }
                            } else {
                                return {
                                    ...item,
                                    disabled: true,
                                }
                            }
                            // if (index == 0) {
                            //     return {
                            //         ...item,
                            //         disabled: false,
                            //     }
                            // } else {
                            //     return {
                            //         ...item,
                            //         disabled: true,
                            //     }
                            // }
                        });
                    }

                    if (!isShopifyConn) {
                        const formattedStepperList = manipulatedStepperList.map((item, index) => {
                            if (item.name == PcSectionSteps.updateShopify) {
                                return {
                                    ...item,
                                    name: PcSectionSteps.connectShopify
                                }
                            } else {
                                return {
                                    ...item,
                                }
                            };
                        });
                        setStepperList(formattedStepperList);
                    } else {
                        // console.log('manipulatedStepperList', manipulatedStepperList)
                        setStepperList(manipulatedStepperList);
                    }
                    if (!isProductUpdateState && !updateObj?.updateAvailable && isShopifyConn && !data?.isInProgress && !isProductFetching && productFetchingStatus?.status != ProductFetchingProgress.failed) {
                        // setStepperList([...stepperList.slice(1)])
                        const filteredSteps = stepperList.filter((item) => {
                            return item.name != PcSectionSteps.updateShopify;
                        });
                        setStepperList(filteredSteps);
                    } else {
                        // console.log('coming here', productFetchingStatus)
                        if (productFetchingStatus?.status) {
                            onShopifyUpdateComplete(productFetchingStatus?.status, productFetchingStatus?.isProductUpdate)
                        }
                    }
                }).catch((error) => {
                    console.log('error');
                })
                await getTabsData();
            } catch (error) {
                console.log('error', error)
                setLoading(false);
            } finally {
                setLoading(false)
            }
        })()
        // console.log("triggering", allConnectedAccounts)

        if (searchParams.get("connectionHub")) {
            // console.log('searchParams')

            searchParams.delete("connectionHub");
            // console.log('searchParams', searchParams.toString())
            if (searchParams.get("showProductState")) {
                // console.log('coming inside propduct fetching loading')
                setProductFetchingLoading(true);
                setUpdate(prev => ({ ...prev, 'isConnected': true }))
            } else {
                connectionToggle();
            }
            setSearchParams(searchParams, { replace: true });
        } else {
            console.log('connection hub not present')
        }
    }, []);

    const formattedTabs = useMemo(() => {
        const toFilterName = [PcSectionSteps.editAccess, PcSectionSteps.connectShopify];
        const filteredTabs = stepperList.filter((item) => {
            if (toFilterName.includes(item.name)) {
                return false;
            } else {
                return true;
            }
        });
        return filteredTabs;
    }, [stepperList]);

    // console.log('stepperList', stepperList);



    const getFinanceStatementTableData = async (showOnlyPnlAdded: boolean = true) => {
        try {
            const data = await RestS.getFinanceStatementTable(false, showOnlyPnlAdded);
            // console.log('data', data);
            return data;
        } catch (error) {
            console.log('error', error);
        }
    }

    useEffect(() => {
        (async () => {
            const isCODAvailable: any = await RestS.isCODAvailable();
            const data: any = await getFinanceStatementTableData(!isCODAvailable);
            // console.log('isCODAvailable', isCODAvailable);
            const removedMetrics = ['Monthly Growth %', 'Gross Profit %', 'CM1 %', 'CM2 %'];
            const allOptions = data?.tableData?.reduce((prev, curVal) => {
                if (!curVal?.isTitle && !removedMetrics.includes(curVal?.name)) {
                    prev.push({
                        label: curVal.name,
                        value: `${TypeOfFinanceOption.financeStatement}_${curVal.id}`,
                        id: curVal.id,
                        type: TypeOfFinanceOption.financeStatement
                    })
                    if (curVal.isExpandable && curVal.subRows.length) {
                        curVal.subRows.forEach((item, index) => {
                            prev.push({
                                label: item?.metricName,
                                value: `${TypeOfFinanceOption.financeMetric}_${item.metricId}`,
                                id: item?.metricId,
                                type: TypeOfFinanceOption.financeMetric
                            })
                        })
                    }
                }
                return prev;
            }, []);
            // console.log('allOPtions', allOptions)
            setFinanceStatementOptions(data?.tableData)
            setMetricOptions(allOptions);

            const { loadingIps = [] } = await RestS.getAccountsFetchingStatusForPc();
            setFetchingIpStatus(loadingIps);
        })();
    }, []);

    const fetchFinanceStatementOptions = async () => {
        try {
            const data: any = await getFinanceStatementTableData();
            // console.log('metricOptionsAPI', data);
            const removedMetrics = ['Monthly Growth %', 'Gross Profit %', 'CM1 %', 'CM2 %'];
            const allOptions = data?.tableData?.reduce((prev, curVal) => {
                if (!curVal?.isTitle && !removedMetrics.includes(curVal?.name)) {
                    prev.push({
                        label: curVal.name,
                        value: `${TypeOfFinanceOption.financeStatement}_${curVal.id}`,
                        id: curVal.id,
                        type: TypeOfFinanceOption.financeStatement
                    })
                    if (curVal.isExpandable && curVal.subRows.length) {
                        curVal.subRows.forEach((item, index) => {
                            prev.push({
                                label: item?.metricName,
                                value: `${TypeOfFinanceOption.financeMetric}_${item.metricId}`,
                                id: item?.metricId,
                                type: TypeOfFinanceOption.financeMetric
                            })
                        })
                    }
                }
                return prev;
            }, []);

            // console.log('allOPtions', allOptions)
            setFinanceStatementOptions(data?.tableData)
        } catch (error) {
            console.log('error', error);
        }
    }

    const refreshTabs = () => {
        getTabsData();
        fetchFinanceStatementOptions();
    }

    const handleNextStep = async () => {
        if (missingProductCosts?.missingCostCount > 0 && stepperLocationObj['/pnltracker/edit/product-costs']['stepIndex'] == activeStep) {
            const nextStepperItem = stepperList[activeStep + 1];
            setSkipToConfig({ link: '', activeStep: activeStep, isFinish: true });
            setShowMissingCostPopup(true)
            return;
        }
        if (stepperList[activeStep].name == PcSectionSteps.addSalesChannels) {
            const success = await handleExternalSubmit();
            // console.log('sucess value', success)
            if (!success) return;
        }
        if (activeStep == stepperList.length - 1) {
            let bodyData = {
                journeyStatus: PcJourneyStatus.completed
            }
            const updatedJourney = await RestS.updatePcJourney(bodyData);
            navigate('/pnltracker/statement', { replace: true });

            try {
                RestS.recalculateFsData(currentUser.companyId, true, true);
            } catch (error) {
                console.log('error in recalculate fs data', error);
            }


        } else {
            const oldActive = activeStep
            // setActiveStep(prev => prev + 1)
            // setCompletedSteps(prev => ({ ...prev, [oldActive + 1]: true }))
            navigate(stepperList[oldActive + 1].link)
        }
    }

    // console.log('tabsData', tabsData);

    const applyProductCosts = async () => {
        if (applyCostsRef?.current) {
            const data = await applyCostsRef?.current?.applyCosts();
        }
    }

    const handleFinish = async () => {
        try {
            let bodyData = {
                journeyStatus: PcJourneyStatus.completed
            }
            const updatedJourney = await RestS.updatePcJourney(bodyData);
            navigate('/pnltracker/statement', { replace: true });

            try {
                RestS.recalculateFsData(currentUser.companyId, true, true);
            } catch (error) {
                console.log('error in recalculate fs data', error);
            }
        } catch (error) {
            console.log('error in handle finish', error);
        }
    }

    const cancelProductCost = () => {
        if (applyCostsRef?.current) {
            applyCostsRef?.current?.cancelCopyCost();
        }
    }

    const handlePreviousStep = () => {
        const oldActive = activeStep;
        if (activeStep == 0) {
            // naviate to get started screen
            if (isFirstTimeUser) {
                navigate('/pnltracker')
            } else {
                // navigate to statement
                navigate('/pnltracker/statement')
            }
        } else {
            navigate(stepperList[oldActive - 1].link)
        }

    }

    const last12MonthsText = useMemo(() => {
        const allMonths = getLast12CYMonthsArr();
        let startMonth = moment(allMonths[allMonths.length - 1], "MM-YYYY").format("YYYY");
        let endMonth = moment(allMonths[0], "MM-YYYY").format("YYYY");

        // console.log('startMonth', startMonth, 'endMonth', endMonth, 'allMonths', allMonths)
        if (startMonth == endMonth) {
            return `Last 12 Months (${moment(allMonths[allMonths.length - 1], "MM-YYYY").format("MMM")} - ${moment(allMonths[0], "MM-YYYY").format("MMM YYYY")})`
        } else {
            return `Last 12 Months (${moment(allMonths[allMonths.length - 1], "MM-YYYY").format("MMM YYYY")} - ${moment(allMonths[0], "MM-YYYY").format("MMM YYYY")})`
        }
    }, []);

    const onShopifyUpdateComplete = (status: ProductFetchingProgress, isProductUpdate: boolean) => {
        let newStepperList = [...stepperList];
        // console.log('status', status);

        if (status == ProductFetchingProgress.inProgress || status == ProductFetchingProgress.failed) {

            newStepperList = stepperList.map((item, index) => {
                if (item.name == PcSectionSteps.updateShopify) {
                    return {
                        ...item,
                        name: !isProductUpdate ? PcSectionSteps.connectShopify : item.name
                    }
                } else if (item.name == PcSectionSteps.addProductCosts) {
                    return {
                        ...item,
                        disabled: true
                    }
                } else {
                    return {
                        ...item,
                        disabled: false
                    }
                }
            });
        } else if (status == ProductFetchingProgress.success) {
            newStepperList = stepperList.map((item, index) => {
                return {
                    ...item,
                    disabled: false
                }
            });
        }
        // console.log('new Stepper List', newStepperList)
        setStepperList(newStepperList)
    }

    // console.log('loading', loading, 'routeLoading', routeLoading);


    const isOtherScreenEnabled = useMemo(() => {
        for (const stepper of stepperList) {
            if (stepper.name == PcSectionSteps.addSalesChannels && !stepper.disabled) {
                return false;
                break;
            }
        }

        return true;
    }, [stepperList]);

    const isNextStepActive = useMemo(() => {
        if (activeStep == stepperList.length - 1 && productFetchingStatus.status == ProductFetchingProgress.success) {
            return false;
        } else {
            if (activeStep == stepperList.length - 1) {
                if (!tabsData?.taxInput || (tabsData?.taxInput['type'] == TaxTypes.fixed && (tabsData?.taxInput['value'] == null || tabsData?.taxInput['value'] == undefined))) {
                    return true;
                } else {
                    return false;
                }
            };
            const nextStep = stepperList[activeStep + 1];
            return nextStep?.disabled;
        }
    }, [activeStep, stepperList]);

    const showTooltipForNextBtn = useMemo(() => {
        if (activeStep == stepperList.length - 1) {
            if (!tabsData?.taxInput || (tabsData?.taxInput['type'] == TaxTypes.fixed && (tabsData?.taxInput['value'] == null || tabsData?.taxInput['value'] == undefined))) {
                return `To finish your setup update Sales Channels.`;
            }
        } else {
            return null;
        }
    }, [activeStep, stepperList]);

    // console.log('showTooltipForNextBtn', showTooltipForNextBtn)


    const isSkipButtonVisible = useMemo(() => {
        const showSkipButtonCases = new Set([PcSectionSteps.editAccess, PcSectionSteps.addExpenses, PcSectionSteps.otherExpenses]);
        const currentStep = stepperList[activeStep];
        if (showSkipButtonCases.has(currentStep.name) && !currentStep.disabled || (currentStep.name == PcSectionSteps.connectShopify && productFetchingStatus?.status == null)) {
            return true;
        } else {
            return false;
        }
    }, [activeStep, stepperList])

    const [fetchingIpStatus, setFetchingIpStatus] = useState([]);

    const { refreshAccountUpdate, refreshAccountFetching } = useAppSelector((state) => state.financeStatement);
    const dispatch = useAppDispatch();

    useEffect(() => {
        (async () => {
            if (refreshAccountFetching) {
                const { loadingIps = [] } = await RestS.getAccountsFetchingStatusForPc();
                setFetchingIpStatus(loadingIps);
            }
        })()
    }, [refreshAccountFetching])


    useEffect(() => {
        (async () => {
            if (refreshAccountUpdate && !pathname.includes("account")) {
                const data = await RestS.fetchProductCostUpdateStatus();
                if (data?.isJobPresent) {
                    if (data?.isInProgress) {
                        onShopifyUpdateComplete(data?.status, data?.isProductUpdate);
                        setProductFetchingStatus({ status: data?.status, isProductUpdate: data?.isProductUpdate, isJobPresent: data?.isJobPresent })
                    } else {
                        setProductFetchingStatus({ status: data?.status, isProductUpdate: data?.isProductUpdate, isJobPresent: data?.isJobPresent })
                        onShopifyUpdateComplete(data?.status, data?.isProductUpdate)
                    }
                }
                dispatch(setRefreshAccountUpdate(false));
            }
        })()

    }, [refreshAccountUpdate]);


    const handleExternalSubmit = async () => {
        // Trigger form submission
        if (taxFormRef.current) {
            const submitValues = await taxFormRef.current.submitTaxForm();
            // console.log('submitValues', submitValues)
            if (!submitValues?.success) {
                setShowTaxesMissingPopup(true)
                return false;
            }
            if (!submitValues?.pristine) {
                const { values } = submitValues;
                // console.log('values', values)
                await RestS.submitTaxInput(values).then((res) => {
                    refreshTabs();
                }).catch((error) => {
                    console.log('error in saving tax', error)
                })
            }
            return true;
        }
    };

    const checkAndSetAvailableUpdate = async () => {
        const prdCostStatus: any = await RestS.getPrdCostUpdateAvailableStatus()
        setPrdCostUpdateAvailable(prdCostStatus?.status ?? false)
    }

    useEffect(() => {
        checkAndSetAvailableUpdate()
    }, [])

    const updatePrdCost = () => {
        setPrdCostUpdateAvailable(false)
        RestS.updatePrdCost()
            .then((res: any) => {
                if (!res?.success) {
                    checkAndSetAvailableUpdate()
                }
            })

    }

    return (
        <>
            <div className={styles.rootContainer}>
                {loading || routeLoading ?
                    <div className={styles.loadingContainer}>
                        <CircularLoader />
                    </div>
                    :
                    <>
                        {pathname?.toLowerCase().includes('copy-costs') ?
                            null
                            :
                            <>
                                {isFirstTimeUser ?
                                    <>
                                        <div className={styles.actionBtnContainer}>
                                            <div className={styles.leftContainer}>
                                                <MeritoButton type='secondary-1' icon={<LeftArrowIcon />} btnText='Back' onClick={handlePreviousStep} />
                                            </div>
                                            <div className={styles.rightContainer}>
                                                {isSkipButtonVisible ? <MeritoButton disabled={isNextStepActive} hover={!isNextStepActive} type={'secondary-2'} onClick={handleNextStep} btnText={'Skip'} /> : null}
                                                {
                                                    prdCostUpdateAvailable && pathname?.toLowerCase()?.includes('product-costs') ?
                                                        <MeritoButton
                                                            disabled={isNextStepActive}
                                                            type={'secondary-2'}
                                                            icon={<IoMdRefresh />}
                                                            isIconToRight={false}
                                                            onClick={updatePrdCost}
                                                            btnText='Update Costs From Shopify'
                                                            appendChild={true}
                                                            isNotification={true}
                                                        >
                                                            <img className={styles.refreshIPLogo} src={integrationPartners?.Shopify?.logo} />
                                                        </MeritoButton>
                                                        : null
                                                }
                                                {showTooltipForNextBtn ?

                                                    <OverlayTrigger
                                                        placement='bottom'
                                                        delay={{ show: 100, hide: 100 }}
                                                        overlay={(props) =>
                                                            <GoalsHubTooltip {...props}>
                                                                {`${showTooltipForNextBtn}`}
                                                            </GoalsHubTooltip>
                                                        }
                                                    >
                                                        <div>
                                                            <MeritoButton disabled={isNextStepActive || (activeStep == stepperList.length - 1 && missingCostsLoading)} type={'primary-1'} icon={activeStep == stepperList.length - 1 ? <RightLongArrowIcon /> : <RightArrowIcon />} isIconToRight={true} onClick={handleNextStep} showLoadingWithText={true} loading={activeStep == stepperList.length - 1 && missingCostsLoading ? true : false} btnText={activeStep == stepperList.length - 1 ? "Finish" : 'Next'} />
                                                        </div>
                                                    </OverlayTrigger>
                                                    :
                                                    <MeritoButton disabled={isNextStepActive || (activeStep == stepperList.length - 1 && missingCostsLoading)} type={'primary-1'} icon={activeStep == stepperList.length - 1 ? <RightLongArrowIcon /> : <RightArrowIcon />} isIconToRight={true} onClick={handleNextStep} showLoadingWithText={true} loading={activeStep == stepperList.length - 1 && missingCostsLoading ? true : false} btnText={activeStep == stepperList.length - 1 ? "Finish" : 'Next'} />
                                                }
                                            </div>
                                        </div>
                                        <div className={styles.stepperRootContainer}>
                                            <CustomStepper connector={<CustomConnector />} alternativeLabel nonLinear activeStep={activeStep}>
                                                {stepperList.map((item, index) => {
                                                    return (
                                                        <Step disabled={item.disabled} completed={completedSteps[index]} key={`${item.name}_${index}`} onClick={async (e) => {
                                                            if (item.disabled) return;
                                                            // console.log(activeStep, stepperLocationObj['/profitability-calculator/edit/product-costs']);
                                                            if (stepperList[activeStep].name == PcSectionSteps.addSalesChannels) {
                                                                const success = await handleExternalSubmit();
                                                                // console.log('sucess value', success)
                                                                if (!success) return;
                                                            }

                                                            if (item.name != PcSectionSteps.addProductCosts && missingProductCosts?.missingCostCount > 0 && stepperLocationObj['/pnltracker/edit/product-costs']['stepIndex'] == activeStep) {
                                                                setSkipToConfig({ link: item.link, activeStep: index, isFinish: false });
                                                                setShowMissingCostPopup(true)
                                                                return;
                                                            } else {
                                                                handleStep(index);
                                                                navigate(item.link)
                                                            }
                                                        }}>
                                                            {item.disabled ?
                                                                <OverlayTrigger
                                                                    placement="bottom"
                                                                    delay={{ show: 100, hide: 100 }}
                                                                    overlay={(props) =>
                                                                        <GoalsHubTooltip {...props}>
                                                                            {!productFetchingStatus?.isProductUpdate && productFetchingStatus?.isJobPresent ? `This will be available after the Shopify data has been fetched.` : !productFetchingStatus?.isProductUpdate ? `Please update your Shopify app.` : `This will be available after the Shopify app has been updated.`}
                                                                        </GoalsHubTooltip>
                                                                    }>
                                                                    <StepLabel style={{ cursor: item.disabled ? "not-allowed" : "pointer" }} StepIconComponent={(props) => <CustomStepIcon {...props} disabled={item.disabled} />}>
                                                                        <div className={`${styles.customStepLabel} ${activeStep == index ? styles.active : ''}`}>
                                                                            {item.name}
                                                                        </div>
                                                                    </StepLabel>
                                                                </OverlayTrigger>
                                                                :
                                                                <StepLabel style={{ cursor: item.disabled ? "not-allowed" : "pointer" }} StepIconComponent={(props) => <CustomStepIcon {...props} disabled={item.disabled} />}>
                                                                    <div className={`${styles.customStepLabel} ${activeStep == index ? styles.active : ''}`}>
                                                                        {item.name}
                                                                    </div>
                                                                </StepLabel>}




                                                        </Step>
                                                    )
                                                })}
                                            </CustomStepper>
                                        </div>
                                    </>
                                    :
                                    <>
                                        <div className={styles.actionBtnContainer}>
                                            <MeritoButton type='secondary-1' icon={<LeftArrowIcon />} btnText='Back' onClick={() => {
                                                navigate('/pnltracker/statement')
                                            }} />
                                            <div className={styles.rightContainer}>
                                                {
                                                    prdCostUpdateAvailable && pathname?.toLowerCase()?.includes('product-costs') ?
                                                        <MeritoButton
                                                            disabled={isNextStepActive}
                                                            type={'secondary-2'}
                                                            icon={<IoMdRefresh />}
                                                            isIconToRight={false}
                                                            onClick={updatePrdCost}
                                                            btnText='Update Costs From Shopify'
                                                            appendChild={true}
                                                            isNotification={true}
                                                        >
                                                            <img className={styles.refreshIPLogo} src={integrationPartners?.Shopify?.logo} />
                                                        </MeritoButton>
                                                        : null
                                                }
                                                <div className={styles.dateTextContainer}>
                                                    {last12MonthsText}
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.tabsRootContainer}>
                                            {formattedTabs.map((item, index) => {
                                                return (
                                                    <div onClick={() => {
                                                        navigate(item.link)
                                                    }} className={`${styles.tabContainer} ${activeTab == item.name ? styles.selected : ''}`}>
                                                        <div className={styles.iconContainer}>
                                                            {item.icon}
                                                        </div>
                                                        <div className={styles.tabText}>
                                                            {item.name}
                                                        </div>
                                                        {
                                                            (item.showRedDot ||
                                                                missingCostItems?.missingSections?.includes(item.name)
                                                                || (item.name == "Product Costs") && missingProductCosts?.missingCostCount > 0)
                                                                ?
                                                                <div className={styles.redDotContainer}>
                                                                    <div className={styles.redDot}>
                                                                    </div>
                                                                </div> : null
                                                        }
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </>
                                }
                            </>


                        }


                        <Routes>
                            <Route path="/account" element={<AccountUpdate productFetchingStatus={productFetchingStatus} setProductFetchingStatus={setProductFetchingStatus} onShopifyUpdateComplete={onShopifyUpdateComplete} productFetchingLoading={productFetchingLoading} connectionToggle={connectionToggle} updateObj={update} handleNextStep={handleNextStep} ipConfigs={ipConfigs} />} />
                            <Route path="/sales-channels" element={
                                <OtherSections fetchingIpStatus={fetchingIpStatus} amzAdsData={amzAdsData} taxInputRef={taxFormRef} ref={scrollToTaxRef} isFirstTimeUser={isFirstTimeUser} productFetchingStatus={productFetchingStatus} key={"sales"} expiredAccountsConfig={expiredAccountsCount} financeStatmentOptions={financeStatementOptions} refreshTabs={refreshTabs} metricOptions={metricOptions} connectionToggle={connectionToggle} tabName={PcSectionSteps.addSalesChannels} tabData={tabsData} />} />
                            <Route path="/product-costs" element={<ProductCosts handleCancelCost={cancelProductCost} ref={applyCostsRef} productCopyState={productCopyState} setProductCopyState={setProductCopyState} defaultCostMissingFilter={costMissingFilter} setDefaultCostMissingFilter={setCostMissingFilter} />}>
                                <Route path='copy-costs/:productId' element={<CopyCosts />} />
                            </Route>
                            <Route path="/direct-expenses" element={<OtherSections fetchingIpStatus={fetchingIpStatus} amzAdsData={amzAdsData} taxInputRef={taxFormRef} ref={dummyRef} isFirstTimeUser={isFirstTimeUser} productFetchingStatus={productFetchingStatus} key={"expenses"} expiredAccountsConfig={expiredAccountsCount} financeStatmentOptions={financeStatementOptions} refreshTabs={refreshTabs} metricOptions={metricOptions} connectionToggle={connectionToggle} tabName={PcSectionSteps.addExpenses} tabData={tabsData} />} />
                            <Route path='/other-expenses' element={<OtherSections fetchingIpStatus={fetchingIpStatus} amzAdsData={amzAdsData} taxInputRef={taxFormRef} ref={dummyRef} isFirstTimeUser={isFirstTimeUser} productFetchingStatus={productFetchingStatus} key={"otherExpenses"} expiredAccountsConfig={expiredAccountsCount} financeStatmentOptions={financeStatementOptions} refreshTabs={refreshTabs} metricOptions={metricOptions} connectionToggle={connectionToggle} tabName={PcSectionSteps.otherExpenses} tabData={tabsData} />} />
                            <Route path="/access" element={<PcAccess />} />
                        </Routes>
                    </>
                }
            </div>


            <Connections
                toggle={connectionToggle}
                visible={connectionVisible}
                clearParams={clearSearchParam}
                connectedState={{ takeState: false }}
            />

            <ConfirmDialog
                modalStyles={{ maxWidth: '450px' }}
                showButton={false}
                visible={showMissingCostPopup}
                toggle={() => {
                    setShowMissingCostPopup(!showMissingCostPopup);
                }}
                showCloseBtn={false}
                size={"md"}
            >
                <div className={styles.confirmationContainer}>
                    <div className={styles.topContainer}>
                        <div className={`${styles.iconContainer} ${styles.warning}`}>
                            <Exclamation />
                        </div>
                        <div className={styles.title}>
                            Costs Missing for {missingProductCosts?.missingCostCount} Products!
                        </div>

                        <div className={styles.warningText}>
                            This will result in inaccurate P&L.
                            {/* {missingProductCost?.missingCostCount == missingProductCost?.productCount ? `Product Costs are missing for ${missingProductCost?.missingCostCount} products, all of which have sales in the last 12 months.` : `Product Costs are missing for ${missingProductCost?.missingCostCount} products out of which ${missingProductCost?.productCount} have sales in last 12 months.`} */}
                            <br />
                            <br />
                        </div>
                    </div>

                    <div className={styles.bottomContainer}>
                        <MeritoButton onClick={() => {
                            if (skipToConfig?.isFinish) {
                                handleFinish();
                            } else {
                                handleStep(skipToConfig.activeStep);
                                navigate(skipToConfig.link);
                            }

                            setShowMissingCostPopup(!showMissingCostPopup);
                        }} size="lg" isRounded={true} btnType="button" type="secondary-2" btnText={missingProductCosts?.missingCostCount > 0 ? 'Ignore' : skipToConfig?.isFinish ? 'Finish' : 'Skip'} />
                        <MeritoButton size="lg" onClick={() => {
                            setCostMissingFilter(prev => !prev)
                            setShowMissingCostPopup(!showMissingCostPopup);
                        }} isRounded={true} btnType="button" type="primary-1" btnText="Update Costs" />
                    </div>
                </div>
            </ConfirmDialog>

            <ConfirmDialog
                modalStyles={{ maxWidth: '450px' }}
                showButton={false}
                visible={showTaxesMissingPopup}
                toggle={() => {
                    setShowTaxesMissingPopup(!showMissingCostPopup);
                }}
                showCloseBtn={false}
                size={"md"}
            >
                <div className={styles.confirmationContainer}>
                    <div className={styles.topContainer}>
                        <div className={styles.iconContainer}>
                            <Exclamation />
                        </div>
                        <div className={styles.title}>
                            Taxes Are Missing!
                        </div>

                        <div className={styles.warningText}>
                            {`To continue, please add taxes for your products.`}
                            <br />
                            <br />
                        </div>
                    </div>

                    <div className={styles.bottomContainer}>
                        <MeritoButton size="lg" onClick={() => {
                            setShowTaxesMissingPopup(!showTaxesMissingPopup);
                            scrollToTaxRef?.current?.scrollToTax();
                        }} isRounded={true} btnType="button" type="primary-1" btnText="Add Taxes" />
                    </div>
                </div>
            </ConfirmDialog>
        </>
    );
}

export default PcSections;